import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, ArrowLeft, ArrowRight, CheckCircle2, Clock, DoorOpen, Mail, Merge, Undo2, User, Users,
} from "lucide-react";
import { api } from "./api";
import { useConfirmation, Patience } from "./Confirmation";
import { Badge, BoutonSombre, Case, ChampChoix, Modale } from "./ui";

/* Fusionner deux cours (demande de l'école, octobre 2026) : deux cours qui manquent
   d'élèves n'en font plus qu'un. Trois étapes, sans rien de technique :
     1. les deux cours côte à côte, on choisit celui qui reste au planning ;
     2. l'aperçu dit exactement ce qui va se passer (rien n'est encore écrit), facture par
        facture : « même prix — facture inchangée » ou « À corriger (+60 CHF) » ;
     3. la fusion (e-mail aux familles si la case est cochée), puis le résumé.
   Une fusion s'annule depuis la fiche du cours fermé (`ModaleAnnulerFusion`). */

const actif = (c) => (c?.statut || "").toLowerCase().startsWith("actif");
const chf = (v) => `${Number(v || 0).toLocaleString("fr-CH")} CHF`;
const dateLisible = (iso) => String(iso || "").slice(0, 10).split("-").reverse().join(".");
const horaire = (c) => `${c.jour || "?"} ${c.debut || ""}–${c.fin || ""}`;

const ORDRE_JOURS = ["lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi", "dimanche"];
const rangJour = (c) => {
  const n = ORDRE_JOURS.indexOf(String(c.jour || "").trim().toLowerCase());
  return n < 0 ? ORDRE_JOURS.length : n;
};
const texte = (v) => String(v || "").trim().toLowerCase();
// Jour, puis discipline, heure de début et prof.
const ordreDesCours = (a, b) => (rangJour(a) - rangJour(b))
  || texte(a.discipline).localeCompare(texte(b.discipline), "fr")
  || texte(a.debut).localeCompare(texte(b.debut))
  || texte(a.professeur).localeCompare(texte(b.professeur), "fr")
  || texte(a.nom).localeCompare(texte(b.nom), "fr");
const majuscule = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Dans la liste : groupés par jour, « Mardi · Hip-hop 7-9 ans · 17:00–18:00 · Claudio » (le
// jour reste dans le libellé pour qu'on le lise aussi une fois la liste refermée).
const optionCours = (c, suite = "") => ({
  valeur: c.id,
  groupe: majuscule(texte(c.jour)) || "Jour non précisé",
  label: [majuscule(texte(c.jour)), c.nom, `${c.debut || "?"}–${c.fin || ""}`, c.professeur]
    .filter(Boolean).join(" · ") + suite,
});
const ecartChf = (v) => `${v > 0 ? "+" : "−"}${chf(Math.abs(v))}`;

/* Ce que la fusion (ou son annulation) fait à la facture d'un élève, dit d'emblée. */
const EffetFacture = ({ d }) => {
  if (d.facture === "a_corriger") return <Badge ton="ambre">facture → À corriger ({ecartChf(d.ecart)})</Badge>;
  if (d.facture === "a_venir") return <Badge ton="bleu">nouveau prix à la prochaine facture ({ecartChf(d.ecart)})</Badge>;
  return <span data-testid="fusion-meme-prix"><Badge ton="vert">même prix — facture inchangée</Badge></span>;
};

/* L'e-mail que recevront les familles : la case, et l'exemple tel qu'il partira. */
function CaseEmail({ p, prevenir, setPrevenir, testid, libelle = "Prévenir les familles par e-mail" }) {
  const avec = (p.familles || []).filter((f) => f.email).length;
  const sans = (p.familles || []).length - avec;
  if (!(p.familles || []).length) return null;
  return (
    <div className="rounded-xl px-4 py-3 mb-3" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }} data-testid={testid}>
      <label className="flex flex-wrap items-center gap-2 text-sm cursor-pointer">
        <Case cochee={prevenir} onChange={setPrevenir} testid={`${testid}-case`} />
        <Mail size={14} style={{ color: "var(--primary)" }} />
        <span className="font-semibold">{libelle}</span>
        <span className="text-xs text-[var(--muted)]">
          — {avec} famille(s), un e-mail par famille (frères et sœurs ensemble){sans ? ` · ${sans} sans adresse e-mail, à prévenir autrement` : ""}
        </span>
      </label>
      {p.email_exemple && (
        <details className="mt-2" data-testid={`${testid}-exemple`}>
          <summary className="text-xs cursor-pointer" style={{ color: "var(--primary)" }}>
            Voir l'e-mail (exemple : {p.email_exemple.famille || p.email_exemple.a})
          </summary>
          <div className="mt-2 rounded-lg bg-white p-3 border text-xs" style={{ borderColor: "var(--border)" }}>
            <div className="mb-2"><span className="text-[var(--muted)]">Sujet :</span> <strong>{p.email_exemple.sujet}</strong></div>
            {/* Le HTML vient du gabarit des Réglages, composé par la console. */}
            <div className="[&_p]:mb-2 [&_a]:underline" dangerouslySetInnerHTML={{ __html: p.email_exemple.html }} />
          </div>
          <p className="text-[11px] text-[var(--muted)] mt-1">Le texte se modifie dans Réglages → Attestations et e-mails → « E-mails envoyés aux familles ».</p>
        </details>
      )}
    </div>
  );
}

export default function ModaleFusion({ tous, depuis, onFermer, onFait }) {
  const { demander } = useConfirmation();
  const ouverts = useMemo(() => (tous || []).filter(actif), [tous]);
  const [premier, setPremier] = useState(depuis?.id || "");
  const [second, setSecond] = useState("");
  const [garde, setGarde] = useState("");
  const [apercu, setApercu] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [plusDePlaces, setPlusDePlaces] = useState(true);
  const [places, setPlaces] = useState("");
  // « Prévenir les familles par e-mail » : cochée par défaut (décision de l'école).
  const [prevenir, setPrevenir] = useState(true);

  const c1 = (tous || []).find((c) => c.id === premier);
  const c2 = (tous || []).find((c) => c.id === second);
  // Les deux listes rangées par jour (lundi → dimanche), puis discipline, heure et prof :
  // c'est ainsi que l'école cherche un cours (demande du 09.10.2026).
  const parJour = useMemo(() => [...ouverts].sort(ordreDesCours), [ouverts]);
  const autres = useMemo(() => parJour.filter((c) => c.id !== premier), [parJour, premier]);

  // Par défaut, le cours qui a le plus d'élèves reste au planning.
  useEffect(() => {
    if (!c1 || !c2) { setGarde(""); return; }
    setGarde((c2.occupe || 0) > (c1.occupe || 0) ? c2.id : c1.id);
  }, [c1, c2]);
  useEffect(() => { setApercu(null); setErreur(""); }, [premier, second, garde]);

  const absorbe = garde && c1 && c2 ? (garde === c1.id ? c2 : c1) : null;
  const gardeC = garde && c1 && c2 ? (garde === c1.id ? c1 : c2) : null;

  const voir = async () => {
    setOccupe(true); setErreur("");
    try {
      const p = await api("/planning/fusion", { method: "POST",
        body: JSON.stringify({ garde: gardeC.id, absorbe: absorbe.id, apercu: true }) });
      setApercu(p);
      setPlaces(String(p.places.propose || ""));
      setPlusDePlaces(p.places.depasse);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  const fusionner = async () => {
    const p = apercu;
    const points = [
      `${p.deplaces.length} élève(s) passent dans « ${p.garde.nom} » (même statut, même date d'inscription).`,
      p.annules.length ? `${p.annules.length} inscription(s) en double annulée(s).` : "",
      `« ${p.absorbe.nom} » sera fermé (Inactif) : il disparaît du site, son historique reste.`,
      p.factures.length ? `${p.factures.length} facture(s) déjà envoyée(s) changent de montant : elles passeront dans « À corriger ».`
        : "Aucune facture à corriger : les montants ne changent pas.",
      prevenir && p.familles.some((f) => f.email)
        ? `${p.familles.filter((f) => f.email).length} famille(s) recevront un e-mail (nouveau jour, heure, salle et prof).`
        : "Aucun e-mail ne part : à toi de prévenir les familles.",
    ].filter(Boolean);
    if (!await demander({
      titre: `Fusionner « ${p.absorbe.nom} » dans « ${p.garde.nom} » ?`,
      ton: "attention",
      texte: "Les élèves changent de cours tout de suite. Pour revenir en arrière : Planning → fiche du cours fermé (« Voir les inactifs ») → « Annuler la fusion ».",
      points,
      valider: "Fusionner les cours",
      annuler: "Pas maintenant",
    })) return;
    setOccupe(true); setErreur("");
    try {
      const corps = { garde: p.garde.id, absorbe: p.absorbe.id, prevenir };
      if (p.places.depasse && plusDePlaces && places) corps.places_max = places;
      setResultat(await api("/planning/fusion", { method: "POST", body: JSON.stringify(corps) }));
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  return (
    <Modale titre="Fusionner deux cours" testid="planning-modale-fusion" large onFermer={resultat ? () => onFait(resultat) : onFermer}
      sous_titre="Les élèves d'un cours passent dans l'autre, qui reste seul au planning. Rien ne bouge avant ta confirmation.">
      {erreur && <div className="rounded-xl px-4 py-3 mb-4 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="fusion-erreur">{erreur}</div>}

      {resultat ? <Resultat r={resultat} onTermine={() => onFait(resultat)} /> : apercu ? (
        <Apercu p={apercu} occupe={occupe} plusDePlaces={plusDePlaces} setPlusDePlaces={setPlusDePlaces}
          places={places} setPlaces={setPlaces} prevenir={prevenir} setPrevenir={setPrevenir}
          onRetour={() => setApercu(null)} onFusionner={fusionner} />
      ) : (
        <>
          <div className="grid md:grid-cols-2 gap-x-5">
            <ChampChoix label="Premier cours" valeur={premier} testid="fusion-cours-1"
              options={[{ valeur: "", label: "Choisir…" }, ...parJour.map((c) => optionCours(c))]}
              onChange={(v) => { setPremier(v); if (v === second) setSecond(""); }} />
            <ChampChoix label="À fusionner avec" valeur={second} testid="fusion-cours-2"
              options={[{ valeur: "", label: "Choisir le second cours…" },
                ...autres.map((c) => optionCours(c, ` · ${c.occupe || 0} élève(s)`))]}
              onChange={setSecond} />
          </div>

          {c1 && c2 && (
            <>
              <p className="text-xs text-[var(--muted)] mb-3">
                Lequel reste <strong>officiellement au planning</strong> (nom, horaire, salle, âge, prof) ? Clique sa carte.
              </p>
              <div className="grid md:grid-cols-2 gap-3 mb-4" data-testid="fusion-cartes">
                {[c1, c2].map((c) => (
                  <CarteChoix key={c.id} c={c} garde={garde === c.id} onChoisir={() => setGarde(c.id)} />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <BoutonSombre onClick={voir} disabled={occupe || !gardeC} testid="fusion-voir">
                  {occupe ? <Patience /> : <ArrowRight size={14} />} Voir ce qui va se passer
                </BoutonSombre>
                <button className="text-sm text-[var(--muted)]" onClick={onFermer} data-testid="fusion-annuler">Annuler</button>
              </div>
            </>
          )}
        </>
      )}
    </Modale>
  );
}

function CarteChoix({ c, garde, onChoisir }) {
  return (
    <button type="button" onClick={onChoisir} data-testid={`fusion-carte-${c.id}`}
      className="text-left rounded-2xl p-4 transition-colors"
      style={{ border: `2px solid ${garde ? "var(--primary)" : "var(--border)"}`, background: garde ? "var(--primary-tint)" : "#fff" }}>
      <div className="flex items-center gap-2 mb-2">
        <span className="w-4 h-4 rounded-full shrink-0 flex items-center justify-center"
          style={{ border: `2px solid ${garde ? "var(--primary)" : "#CBD5E1"}` }}>
          {garde && <span className="w-2 h-2 rounded-full" style={{ background: "var(--primary)" }} />}
        </span>
        {garde ? <Badge ton="vert">Reste au planning</Badge> : <Badge ton="ambre">Ses élèves passent dans l'autre, puis il se ferme</Badge>}
      </div>
      <div className="text-sm font-semibold leading-tight">{c.nom}</div>
      <div className="text-xs text-[var(--muted)] mt-1.5 space-y-1">
        <div className="flex items-center gap-1.5"><Clock size={12} /> {horaire(c)}</div>
        <div className="flex items-center gap-1.5"><DoorOpen size={12} /> {c.salle || "—"} · {c.age_affiche || "âge non précisé"}</div>
        <div className="flex items-center gap-1.5"><User size={12} /> {c.professeur || "—"}</div>
        <div className="flex items-center gap-1.5"><Users size={12} />
          {c.occupe || 0}/{c.places_max || "—"} place(s) occupée(s)
          {c.nb_attente > 0 ? ` · ${c.nb_attente} en attente` : ""}{c.nb_essais > 0 ? ` · ${c.nb_essais} essai(s)` : ""}
        </div>
      </div>
    </button>
  );
}

function Apercu({ p, occupe, plusDePlaces, setPlusDePlaces, places, setPlaces, prevenir, setPrevenir, onRetour, onFusionner }) {
  const changePrix = p.deplaces.filter((d) => d.prix_change);
  const apres = p.places.depasse && plusDePlaces && places ? Number(places) : p.places.places_max;
  return (
    <div data-testid="fusion-apercu">
      <div className="rounded-2xl px-4 py-3 mb-4 flex flex-wrap items-center gap-2 text-sm" style={{ background: "#FBFCFD", border: "1px solid var(--border)" }}>
        <span className="font-semibold">{p.absorbe.nom}</span>
        <span className="text-[var(--muted)] text-xs">({horaire(p.absorbe)})</span>
        <ArrowRight size={15} style={{ color: "var(--primary)" }} />
        <span className="font-semibold">{p.garde.nom}</span>
        <span className="text-[var(--muted)] text-xs">({horaire(p.garde)})</span>
      </div>

      {p.rien_a_reprendre && (
        <p className="text-sm mb-4" data-testid="fusion-rien">
          {p.deja_fusionne ? "Ce cours est déjà fusionné : il n'y a plus rien à reprendre."
            : `« ${p.absorbe.nom} » n'a plus d'élève à reprendre : la fusion le ferme simplement.`}
        </p>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <Mesure valeur={p.deplaces.length} label="élèves qui passent" testid="fusion-nb-deplaces" />
        <Mesure valeur={p.annules.length} label="doublons annulés" />
        <Mesure valeur={`${p.places.occupe_apres}/${apres || "—"}`} label="places après" alerte={!!apres && p.places.occupe_apres > apres} testid="fusion-places-apres" />
        <Mesure valeur={p.factures.length} label="factures à corriger" testid="fusion-nb-factures" />
      </div>

      {p.changements.length > 0 && (
        <div className="text-xs mb-3" data-testid="fusion-changements">
          <div className="font-semibold mb-1">Ce qui change pour les élèves qui passent</div>
          <ul className="space-y-0.5 text-[var(--muted)]">{p.changements.map((c) => <li key={c}>· {c}</li>)}</ul>
        </div>
      )}

      {p.avertissements.length > 0 && (
        <div className="rounded-xl px-4 py-3 mb-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }} data-testid="fusion-avertissements">
          <div className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: "#B45309" }}><AlertTriangle size={13} /> À savoir avant de fusionner</div>
          <ul className="text-xs space-y-1">{p.avertissements.map((a) => <li key={a}>· {a}</li>)}</ul>
        </div>
      )}

      {p.places.depasse && (
        <label className="flex flex-wrap items-center gap-2 text-sm mb-4 cursor-pointer" data-testid="fusion-places">
          <Case cochee={plusDePlaces} onChange={setPlusDePlaces} testid="fusion-places-case" />
          <span>Passer les places de « {p.garde.nom} » de {p.places.places_max} à</span>
          <input type="number" className="input-field" style={{ width: 80 }} value={places} min={p.places.occupe_apres}
            onChange={(e) => setPlaces(e.target.value)} disabled={!plusDePlaces} data-testid="fusion-places-nombre" />
        </label>
      )}

      <Liste titre={`Élèves qui passent dans « ${p.garde.nom} »`} n={p.deplaces.length} ouvert testid="fusion-liste-deplaces">
        {p.deplaces.map((d) => (
          <li key={d.inscription_id} className="flex flex-wrap items-center gap-x-2">
            <span className="text-[var(--text)]">{d.eleve}</span>
            <span>· {d.statut}{d.carte10 ? " · carte 10 cours" : ""}</span>
            <span style={{ color: d.prix_change ? "#B45309" : undefined, fontWeight: d.prix_change ? 600 : 400 }}>
              · {d.prix_change ? `${chf(d.ancien_montant)} → ${chf(d.nouveau_montant)}` : chf(d.nouveau_montant)}
            </span>
            <EffetFacture d={d} />
          </li>
        ))}
      </Liste>
      {changePrix.length > 0 && (
        <p className="text-[11px] text-[var(--muted)] -mt-1 mb-2">
          Le prix suit le cours gardé (forfait ou durée), comme pour un « Changer de cours » ; un écart se règle ensuite dans « À corriger ».
        </p>
      )}
      <Liste titre="Inscriptions en double annulées" n={p.annules.length} testid="fusion-liste-annules">
        {p.annules.map((a) => (
          <li key={a.inscription_id} className="flex flex-wrap items-center gap-x-2">
            <span><span className="text-[var(--text)]">{a.eleve}</span> — {a.raison}</span>
            {a.montant_retire > 0 && <EffetFacture d={a} />}
          </li>
        ))}
      </Liste>
      <Liste titre={`Restent dans l'historique de « ${p.absorbe.nom} »`} n={p.restent.length} testid="fusion-liste-restent">
        {p.restent.map((a) => <li key={a.inscription_id}><span className="text-[var(--text)]">{a.eleve}</span> — {a.raison}</li>)}
      </Liste>
      <Liste titre="Cours d'essai à venir qui suivent" n={p.essais.length} testid="fusion-liste-essais">
        {p.essais.map((e) => (
          <li key={e.essai_id}><span className="text-[var(--text)]">{e.eleve}</span> — {dateLisible(e.date_avant)}
            {e.date_change ? ` → ${dateLisible(e.date_apres)} (famille à prévenir)` : ""}{e.doublon ? " · déjà prévu dans l'autre cours : écarté" : ""}</li>
        ))}
      </Liste>
      <Liste titre="Absences annoncées qui suivent" n={p.absences.length} testid="fusion-liste-absences">
        {p.absences.map((a) => <li key={a.absence_id}><span className="text-[var(--text)]">{a.eleve}</span> — {a.periode}{a.a_verifier ? " · à vérifier (jour différent)" : ""}</li>)}
      </Liste>
      <Liste titre="Familles à prévenir" n={p.familles.length} testid="fusion-liste-familles">
        {p.familles.map((f) => <li key={`${f.email}-${f.eleves.join()}`}><span className="text-[var(--text)]">{f.responsable || "—"}</span> ({f.eleves.join(", ")}) · {f.email || "sans e-mail"}{f.telephone ? ` · ${f.telephone}` : ""}</li>)}
      </Liste>

      <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
        <CaseEmail p={p} prevenir={prevenir} setPrevenir={setPrevenir} testid="fusion-email" />
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <BoutonSombre onClick={onFusionner} disabled={occupe} testid="fusion-confirmer">
          {occupe ? <Patience /> : <Merge size={14} />} Fusionner les cours
        </BoutonSombre>
        <button className="text-sm text-[var(--muted)]" onClick={onRetour} data-testid="fusion-retour">Retour au choix</button>
      </div>
    </div>
  );
}

function Resultat({ r, onTermine }) {
  return (
    <div data-testid="fusion-resultat">
      <div className="rounded-xl px-4 py-3 mb-4 flex gap-3 items-start text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}>
        <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
        <div data-testid="fusion-message">{r.message}</div>
      </div>
      <div className="text-xs space-y-2 mb-4">
        <div className="font-semibold">Et maintenant</div>
        <ul className="space-y-1 text-[var(--muted)]">
          {r.fait.emails > 0
            ? <li>· {r.emails?.mode_reel === false
              ? `${r.fait.emails} e-mail(s) préparé(s) — version de test : aucun n'est parti vers les familles`
              : `${r.fait.emails} famille(s) ont reçu un e-mail`}{(r.emails?.sans_email || []).length ? ` ; sans adresse e-mail, à prévenir autrement : ${r.emails.sans_email.join(", ")}` : ""}{(r.emails?.refuses || []).length ? ` ; e-mail refusé par le service d'envoi, à prévenir autrement : ${r.emails.refuses.join(", ")}` : ""}.</li>
            : <li>· Prévenir les familles : Envois groupés → Cours → « {r.garde.nom} » (tout le groupe), ou une par une ci-dessous.</li>}
          <li>· {r.garde.lien_whatsapp
            ? <>Leur envoyer le lien du groupe WhatsApp de « {r.garde.nom} » : <span className="text-[var(--text)] break-all">{r.garde.lien_whatsapp}</span></>
            : <>« {r.garde.nom} » n'a pas de lien de groupe WhatsApp : à coller dans sa fiche si besoin.</>}</li>
          {r.fait.factures_a_corriger > 0
            ? <li>· Facturation → « À corriger » : valider les factures dont le montant change.</li>
            : <li>· Factures : rien à corriger, les montants ne changent pas.</li>}
          <li>· Liste : réimprimer la feuille de « {r.garde.nom} » (les élèves arrivés y sont marqués « fusion », pas NOUVEAU).</li>
          <li>· Revenir en arrière : fiche de « {r.absorbe.nom} » (Planning → Voir les inactifs) → « Annuler la fusion ».</li>
        </ul>
      </div>
      {r.familles.length > 0 && (
        <Liste titre="Familles à prévenir" n={r.familles.length} ouvert testid="fusion-resultat-familles">
          {r.familles.map((f) => <li key={`${f.email}-${f.eleves.join()}`}><span className="text-[var(--text)]">{f.responsable || "—"}</span> ({f.eleves.join(", ")}) · {f.email || "sans e-mail"}{f.telephone ? ` · ${f.telephone}` : ""}</li>)}
        </Liste>
      )}
      <BoutonSombre onClick={onTermine} testid="fusion-termine">Terminé</BoutonSombre>
    </div>
  );
}

function Liste({ titre, n, children, ouvert, testid }) {
  if (!n) return null;
  return (
    <details className="mb-2" open={ouvert} data-testid={testid}>
      <summary className="text-xs cursor-pointer font-semibold" style={{ color: "var(--primary)" }}>{titre} ({n})</summary>
      <ul className="mt-2 mb-1 space-y-1 text-[11px] text-[var(--muted)] max-h-48 overflow-auto">{children}</ul>
    </details>
  );
}

const Mesure = ({ valeur, label, alerte, testid }) => (
  <div className="bg-white rounded-xl px-3 py-2 border" style={{ borderColor: alerte ? "#FCA5A5" : "var(--border)" }} data-testid={testid}>
    <div className="font-display text-xl leading-none" style={{ color: alerte ? "#B91C1C" : undefined }}>{valeur}</div>
    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--muted)] mt-1">{label}</div>
  </div>
);

/* Annuler une fusion (demande de l'école, 09.10.2026) : ouverte depuis la fiche du cours
   fermé (« Fusionné dans … ») ou du cours gardé. L'aperçu dit ce qui sera remis en place,
   ce qui ne bouge pas (modifié depuis la fusion) et ce que deviennent les factures ; rien
   n'est écrit avant la confirmation. */
export function ModaleAnnulerFusion({ absorbe, onFermer, onFait }) {
  const { demander } = useConfirmation();
  const [p, setP] = useState(null);
  const [resultat, setResultat] = useState(null);
  const [erreur, setErreur] = useState("");
  const [occupe, setOccupe] = useState(false);
  const [prevenir, setPrevenir] = useState(true);

  useEffect(() => {
    let vivant = true;
    api("/planning/fusion/annuler", { method: "POST", body: JSON.stringify({ absorbe, apercu: true }) })
      .then((d) => { if (vivant) setP(d); })
      .catch((e) => { if (vivant) setErreur(e.message); });
    return () => { vivant = false; };
  }, [absorbe]);

  const annuler = async () => {
    const points = [
      `${p.retours.length} élève(s) retournent dans « ${p.absorbe.nom} » (même statut, prix d'avant).`,
      p.retablis.length ? `${p.retablis.length} inscription(s) en double retrouvent leur statut.` : "",
      p.cours.rouvert ? `« ${p.absorbe.nom} » rouvre (Actif) et revient sur le site.` : "",
      p.ignores.length ? `${p.ignores.length} élément(s) modifié(s) depuis la fusion ne bougent pas.` : "",
      p.factures.length ? `${p.factures.length} facture(s) déjà envoyée(s) changent de montant : « À corriger ».`
        : "Aucune facture à corriger : les montants ne changent pas.",
      prevenir && p.familles.some((f) => f.email)
        ? `${p.familles.filter((f) => f.email).length} famille(s) recevront un e-mail : « le cours reprend comme avant ».`
        : "Aucun e-mail ne part : à toi de prévenir les familles.",
    ].filter(Boolean);
    if (!await demander({
      titre: `Annuler la fusion de « ${p.absorbe.nom} » ?`,
      ton: "attention",
      texte: `Les élèves repartent de « ${p.garde.nom} » tout de suite.`,
      points,
      valider: "Annuler la fusion",
      annuler: "Ne rien changer",
    })) return;
    setOccupe(true); setErreur("");
    try {
      setResultat(await api("/planning/fusion/annuler", { method: "POST",
        body: JSON.stringify({ absorbe, prevenir }) }));
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  return (
    <Modale titre="Annuler la fusion" testid="planning-modale-annuler-fusion" large
      onFermer={resultat ? () => onFait(resultat) : onFermer}
      sous_titre="Les élèves retournent dans leur cours d'avant, qui rouvre. Rien ne bouge avant ta confirmation.">
      {erreur && <div className="rounded-xl px-4 py-3 mb-4 text-sm" style={{ background: "#FEF2F2", border: "1px solid #FECACA", color: "#B91C1C" }} data-testid="annuler-fusion-erreur">{erreur}</div>}
      {!p && !erreur && <p className="text-sm text-[var(--muted)]"><Patience /> Lecture de la fusion…</p>}
      {resultat ? (
        <div data-testid="annuler-fusion-resultat">
          <div className="rounded-xl px-4 py-3 mb-4 flex gap-3 items-start text-sm" style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#15803D" }}>
            <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
            <div data-testid="annuler-fusion-message">{resultat.message}</div>
          </div>
          {resultat.ignores.length > 0 && (
            <Liste titre="À régler à la main (modifié depuis la fusion)" n={resultat.ignores.length} ouvert testid="annuler-fusion-resultat-ignores">
              {resultat.ignores.map((x, k) => <li key={k}><span className="text-[var(--text)]">{x.eleve}</span> ({x.quoi}) — {x.raison}</li>)}
            </Liste>
          )}
          <BoutonSombre onClick={() => onFait(resultat)} testid="annuler-fusion-termine">Terminé</BoutonSombre>
        </div>
      ) : p && (
        <div data-testid="annuler-fusion-apercu">
          <div className="rounded-2xl px-4 py-3 mb-4 flex flex-wrap items-center gap-2 text-sm" style={{ background: "#FBFCFD", border: "1px solid var(--border)" }}>
            <span className="font-semibold">{p.garde.nom}</span>
            <span className="text-[var(--muted)] text-xs">({horaire(p.garde)})</span>
            <ArrowLeft size={15} style={{ color: "var(--primary)" }} />
            <span className="text-xs text-[var(--muted)]">fusion du {p.fusions.map((f) => dateLisible(f.le)).join(", ")} annulée :</span>
            <ArrowRight size={15} style={{ color: "var(--primary)" }} />
            <span className="font-semibold">{p.absorbe.nom}</span>
            <span className="text-[var(--muted)] text-xs">({horaire(p.absorbe)})</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
            <Mesure valeur={p.retours.length} label="élèves qui reviennent" testid="annuler-fusion-nb-retours" />
            <Mesure valeur={p.retablis.length} label="doublons rétablis" />
            <Mesure valeur={p.ignores.length} label="modifiés depuis : ne bougent pas" alerte={p.ignores.length > 0} testid="annuler-fusion-nb-ignores" />
            <Mesure valeur={p.factures.length} label="factures à corriger" testid="annuler-fusion-nb-factures" />
          </div>

          <ul className="text-xs mb-3 space-y-0.5 text-[var(--muted)]" data-testid="annuler-fusion-cours">
            <li>· « {p.absorbe.nom} » : {p.cours.rouvert ? "rouvre (Actif), revient sur le site ; la mention « Fusionné dans … » est retirée." : "reste fermé (il l'était déjà avant la fusion)."}</li>
            {p.places && <li>· Places de « {p.garde.nom} » : {p.places.actuel} → {p.places.avant} (comme avant la fusion).</li>}
          </ul>

          {(p.avertissements.length > 0 || p.limites.length > 0) && (
            <div className="rounded-xl px-4 py-3 mb-4" style={{ background: "#FFFBEB", border: "1px solid #FDE68A", color: "#92400E" }} data-testid="annuler-fusion-avertissements">
              <div className="flex items-center gap-2 text-xs font-semibold mb-1" style={{ color: "#B45309" }}><AlertTriangle size={13} /> À savoir avant d'annuler</div>
              <ul className="text-xs space-y-1">{[...p.limites, ...p.avertissements].map((a) => <li key={a}>· {a}</li>)}</ul>
            </div>
          )}

          <Liste titre={`Élèves qui retournent dans « ${p.absorbe.nom} »`} n={p.retours.length} ouvert testid="annuler-fusion-liste-retours">
            {p.retours.map((d) => (
              <li key={d.inscription_id} className="flex flex-wrap items-center gap-x-2">
                <span className="text-[var(--text)]">{d.eleve}</span>
                <span>· {d.statut}</span>
                <span style={{ color: Math.abs(d.ecart) > 0.01 ? "#B45309" : undefined, fontWeight: Math.abs(d.ecart) > 0.01 ? 600 : 400 }}>
                  · {Math.abs(d.ecart) > 0.01 ? `${chf(d.montant_actuel)} → ${chf(d.montant_retour)}` : chf(d.montant_retour)}
                </span>
                <EffetFacture d={d} />
              </li>
            ))}
          </Liste>
          <Liste titre="Inscriptions en double qui retrouvent leur statut" n={p.retablis.length} testid="annuler-fusion-liste-retablis">
            {p.retablis.map((d) => (
              <li key={d.inscription_id} className="flex flex-wrap items-center gap-x-2">
                <span><span className="text-[var(--text)]">{d.eleve}</span> — « {d.cours} » : Annulé → {d.statut || "Inscrit"}</span>
                {d.montant > 0 && <EffetFacture d={d} />}
              </li>
            ))}
          </Liste>
          <Liste titre="Cours d'essai à venir qui reviennent" n={p.essais.length} testid="annuler-fusion-liste-essais">
            {p.essais.map((e) => <li key={e.essai_id}><span className="text-[var(--text)]">{e.eleve}</span> — {dateLisible(e.date_retour)}{e.date_retour !== e.date_actuelle ? ` (au lieu du ${dateLisible(e.date_actuelle)})` : ""}</li>)}
          </Liste>
          <Liste titre="Absences annoncées qui reviennent" n={p.absences.length} testid="annuler-fusion-liste-absences">
            {p.absences.map((a) => <li key={a.absence_id}><span className="text-[var(--text)]">{a.eleve}</span> — {a.periode}</li>)}
          </Liste>
          <Liste titre="Modifié depuis la fusion : ne bouge pas (à régler à la main)" n={p.ignores.length} ouvert testid="annuler-fusion-liste-ignores">
            {p.ignores.map((x, k) => <li key={k}><span className="text-[var(--text)]">{x.eleve}</span> ({x.quoi}) — {x.raison}</li>)}
          </Liste>
          <Liste titre="Familles à prévenir" n={p.familles.length} testid="annuler-fusion-liste-familles">
            {p.familles.map((f) => <li key={`${f.email}-${f.eleves.join()}`}><span className="text-[var(--text)]">{f.responsable || "—"}</span> ({f.eleves.join(", ")}) · {f.email || "sans e-mail"}{f.telephone ? ` · ${f.telephone}` : ""}</li>)}
          </Liste>

          <div className="mt-5 pt-4 border-t" style={{ borderColor: "var(--border)" }}>
            <CaseEmail p={p} prevenir={prevenir} setPrevenir={setPrevenir} testid="annuler-fusion-email" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <BoutonSombre onClick={annuler} disabled={occupe || (!p.retours.length && !p.retablis.length && !p.cours.rouvert && !p.essais.length)} testid="annuler-fusion-confirmer">
              {occupe ? <Patience /> : <Undo2 size={14} />} Annuler la fusion
            </BoutonSombre>
            <button className="text-sm text-[var(--muted)]" onClick={onFermer} data-testid="annuler-fusion-fermer">Ne rien changer</button>
          </div>
        </div>
      )}
    </Modale>
  );
}
